/**
 * 헤더/드로어 BGM: .playlist 내부 audio 재생, .sound-title 갱신, 볼륨/재생/일시정지/이전·다음 트랙.
 * 트랙이 없으면 .sound-wrap에 --no-tracks 클래스로 숨김.
 */
const WRAP_SELECTOR = ".sound-wrap";
const PLAYLIST_SELECTOR = ".playlist";
const TITLE_SELECTOR = ".sound-title";
const PLAY_SELECTOR = ".play";
const TOGGLE_SELECTOR = ".sound-toggle";
const PREV_SELECTOR = ".sound-prev";
const NEXT_SELECTOR = ".sound-next";
const NO_TRACKS_CLASS = "sound-wrap--no-tracks";

/** sound-wrap 안에 있는 컨트롤만 선택 (다른 페이지의 .play 등과 충돌 방지) */
const PLAY_IN_WRAP = `${WRAP_SELECTOR} ${PLAY_SELECTOR}`;
const TOGGLE_IN_WRAP = `${WRAP_SELECTOR} ${TOGGLE_SELECTOR}`;
const PREV_IN_WRAP = `${WRAP_SELECTOR} ${PREV_SELECTOR}`;
const NEXT_IN_WRAP = `${WRAP_SELECTOR} ${NEXT_SELECTOR}`;

let currentIndex = 0;
let audios: HTMLAudioElement[] = [];
let muted = false;

function getAudios(): HTMLAudioElement[] {
  const firstPlaylist = document.querySelector(PLAYLIST_SELECTOR);
  if (!firstPlaylist) return [];
  return Array.from(firstPlaylist.querySelectorAll<HTMLAudioElement>("audio"));
}

function updateTitle(title: string): void {
  document.querySelectorAll(TITLE_SELECTOR).forEach((el) => {
    el.textContent = title;
  });
}

function getCurrentAudio(): HTMLAudioElement | null {
  return audios[currentIndex] ?? null;
}

function syncPausedState(paused: boolean): void {
  document.querySelectorAll(WRAP_SELECTOR).forEach((wrap) => {
    wrap.classList.toggle("is-paused", paused);
  });
}

function syncMutedState(): void {
  const audio = getCurrentAudio();
  if (audio) audio.muted = muted;
  document.querySelectorAll(TOGGLE_SELECTOR).forEach((btn) => {
    const icon = btn.querySelector("i");
    if (icon) {
      icon.classList.toggle("on", !muted);
      icon.classList.toggle("off", muted);
    }
  });
}

function playCurrent(): void {
  const audio = getCurrentAudio();
  if (!audio) return;
  audio.play().catch(() => {});
  const title = audio.getAttribute("data-title") ?? "";
  updateTitle(title);
  syncPausedState(false);
}

function pauseCurrent(): void {
  const audio = getCurrentAudio();
  if (audio) audio.pause();
  syncPausedState(true);
}

function goPrev(): void {
  if (audios.length === 0) return;
  const audio = getCurrentAudio();
  if (audio) audio.pause();
  currentIndex = currentIndex <= 0 ? audios.length - 1 : currentIndex - 1;
  playCurrent();
}

function goNext(): void {
  if (audios.length === 0) return;
  const audio = getCurrentAudio();
  if (audio) audio.pause();
  currentIndex = currentIndex >= audios.length - 1 ? 0 : currentIndex + 1;
  playCurrent();
}

export function initHeaderBgm(): void {
  audios = getAudios();

  document.querySelectorAll(WRAP_SELECTOR).forEach((wrap) => {
    if (audios.length === 0) {
      wrap.classList.add(NO_TRACKS_CLASS);
      return;
    }
    wrap.classList.remove(NO_TRACKS_CLASS);
  });

  if (audios.length === 0) return;

  currentIndex = 0;
  const first = audios[0];
  updateTitle(first.getAttribute("data-title") ?? "");
  syncPausedState(true);
  syncMutedState();

  audios.forEach((audio, i) => {
    audio.addEventListener("ended", () => {
      if (i === currentIndex) {
        if (audios.length > 1) goNext();
        else syncPausedState(true);
      }
    });
  });

  document.querySelectorAll(WRAP_SELECTOR).forEach((wrap) => {
    wrap.addEventListener("click", (e) => {
      const target = e.target as Element | null;
      const playEl = target?.closest?.(PLAY_SELECTOR);
      if (!playEl || !wrap.contains(playEl)) return;
      e.preventDefault();
      e.stopPropagation();
      const a = getCurrentAudio();
      if (!a) return;
      if (a.paused) playCurrent();
      else pauseCurrent();
    });
  });

  document.querySelectorAll(TOGGLE_IN_WRAP).forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      muted = !muted;
      syncMutedState();
    });
  });

  document.querySelectorAll(PREV_IN_WRAP).forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      goPrev();
    });
  });

  document.querySelectorAll(NEXT_IN_WRAP).forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      goNext();
    });
  });
}
